import {
  BlockConfig,
  zeroIOPS,
  OperationTypes,
  ContentTypes,
  Block,
  IOPS,
  multiplyIOPS,
  flattenIOPS,
  AUTO_SCALING_GROUP_SCALE_UP_THRESHOLD,
  AUTO_SCALING_GROUP_SCALE_DOWN_THRESHOLD,
} from "../block-base";
import { BlockTypes } from "../block-types";

export const autoScalingGroupSideEffects = (block: Block, iops: IOPS): Block => {
  // Make a copy of the block to avoid mutating the original
  let finalBlock = { ...block };

  let finalDynamicProperties = finalBlock.dynamicProperties || { "server-count": 1 };

  // Flatten the IOPS to get all operation-content type pairs
  const flatIOPS = flattenIOPS(iops);
  const flatCapacity = flattenIOPS(finalBlock.inputCapacity);

  // Collect all usage ratios for each IOPS type (excluding 'total')
  const usageRatios: number[] = [];
  Object.keys(flatIOPS).forEach((key) => {
    if (key === "total") return;
    const usage = flatIOPS[key];
    const capacity = flatCapacity[key] || 0;
    if (capacity > 0) {
      usageRatios.push(usage / capacity);
    }
  });

  // Determine scale up/down logic
  let shouldScaleUp = usageRatios.some((ratio) => ratio > AUTO_SCALING_GROUP_SCALE_UP_THRESHOLD);
  let shouldScaleDown =
    usageRatios.length > 0 && usageRatios.every((ratio) => ratio < AUTO_SCALING_GROUP_SCALE_DOWN_THRESHOLD);

  // Scale up if any usage is above the up threshold
  if (shouldScaleUp) {
    const currentServerCount = finalDynamicProperties["server-count"] || 1;
    finalDynamicProperties["server-count"] = currentServerCount + 1;
  }
  // Scale down if all usages are below the down threshold
  else if (shouldScaleDown) {
    const currentServerCount = finalDynamicProperties["server-count"] || 1;
    if (currentServerCount > 1) {
      finalDynamicProperties["server-count"] = currentServerCount - 1;
    }
  }

  // Update the block's dynamic properties
  finalBlock.dynamicProperties = finalDynamicProperties;

  const inputCapacity = autoScalingGroupConfig.inputCapacity;
  const processingCapacity = autoScalingGroupConfig.processingCapacity;

  // Update the input and processing capacities based on the new server count
  const serverCount = finalDynamicProperties["server-count"] || 1;
  finalBlock.inputCapacity = multiplyIOPS(inputCapacity, serverCount);
  finalBlock.processingCapacity = multiplyIOPS(processingCapacity, serverCount);

  return finalBlock;
};

export const autoScalingGroupConfig: BlockConfig = {
  displayName: "Auto Scaling Group",
  imagePath: "",
  isInitiator: true,
  isTerminator: true,
  isInterceptor: false,
  allowedInputTypes: new Set([
    BlockTypes.USER,
    BlockTypes.LOAD_BALANCER,
    BlockTypes.NETWORK_LOAD_BALANCER,
    BlockTypes.LAMBDA,
    BlockTypes.AUTO_SCALING_GROUP,
    BlockTypes.QUEUE,
    BlockTypes.PASSTHROUGH,
  ]),
  allowedOutputTypes: new Set([
    BlockTypes.DATABASE,
    BlockTypes.DB_READ_REPLICA,
    BlockTypes.LAMBDA,
    BlockTypes.AUTO_SCALING_GROUP,
    BlockTypes.QUEUE,
    BlockTypes.NETWORK_LOAD_BALANCER,
    BlockTypes.GRAPH_DB,
    BlockTypes.DOCUMENT_DB,
    BlockTypes.S3_BUCKET,
    BlockTypes.PASSTHROUGH,
  ]),
  inputCapacity: {
    total: 1500,
    byOperationType: {
      [`${OperationTypes.READ}`]: {
        total: 900,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 300,
          [`${ContentTypes.STATIC}`]: 300,
          [`${ContentTypes.STATIC_MEDIA}`]: 300,
        },
      },
      [`${OperationTypes.WRITE}`]: {
        total: 600,
        byContentType: {
          [`${ContentTypes.DYNAMIC}`]: 400,
          [`${ContentTypes.STATIC_MEDIA}`]: 200,
        },
      },
    },
  },
  processingCapacity: zeroIOPS(),
  purchasePrice: 500,
  sellPrice: 50,
  costPerCycle: 0.6,
  outputConnectorLimits: {},
  blockSideEffects: (block: Block, iops: IOPS) => autoScalingGroupSideEffects(block, iops),
};
